import { HeroCarousel } from '../components/HeroCarousel';
import type { Media } from '@wineryxplorer/shared';

// Mock media data for demonstration
const mockHeroImages: Media[] = [
  {
    id: '1',
    cloudinaryPublicId: 'demo1',
    cloudinaryUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=400&fit=crop',
    entityType: 'venue',
    entityId: 'demo-venue',
    uploadedBy: 'demo-user',
    filename: 'vineyard1.jpg',
    mimeType: 'image/jpeg',
    width: 1200,
    height: 400,
    sizeBytes: 150000,
    status: 'active',
    isPublished: true,
    tags: ['vineyard', 'landscape'],
    caption: 'Beautiful vineyard landscape',
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: '2',
    cloudinaryPublicId: 'demo2',
    cloudinaryUrl: 'https://images.unsplash.com/photo-1474722883778-792e7990302f?w=1200&h=400&fit=crop',
    entityType: 'venue',
    entityId: 'demo-venue',
    uploadedBy: 'demo-user',
    filename: 'winery-interior.jpg',
    mimeType: 'image/jpeg',
    width: 1200,
    height: 400,
    sizeBytes: 180000,
    status: 'active',
    isPublished: true,
    tags: ['interior', 'tasting-room'],
    caption: 'Elegant tasting room interior',
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: '3',
    cloudinaryPublicId: 'demo3',
    cloudinaryUrl: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200&h=400&fit=crop',
    entityType: 'venue',
    entityId: 'demo-venue',
    uploadedBy: 'demo-user',
    filename: 'wine-barrels.jpg',
    mimeType: 'image/jpeg',
    width: 1200,
    height: 400,
    sizeBytes: 160000,
    status: 'active',
    isPublished: true,
    tags: ['barrels', 'cellar'],
    caption: 'Traditional wine barrel storage',
    createdAt: new Date(),
    updatedAt: new Date(),
  },
];

export const HeroCarouselDemo = () => {
  return (
    <div className="px-4 py-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Hero Carousel Component Demo</h1>
      
      <div className="space-y-8">
        {/* Multiple Images Carousel */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Multiple Images (Auto-advance with hover pause)</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <HeroCarousel
              images={mockHeroImages}
              venueName="Demo Winery & Vineyard Estate"
              venueType="winery"
              className="h-80"
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            • Auto-advances every 4 seconds<br/>
            • Hover to pause auto-advance<br/>
            • Cross-dissolve transition (800ms)<br/>
            • Prevents transition conflicts<br/>
            • Click arrows or dots to navigate manually<br/>
            • Wraparound navigation (last→first, first→last)<br/>
            • <strong>Mobile:</strong> Swipe left/right to navigate<br/>
            • <strong>Mobile:</strong> Touch-friendly controls with larger targets<br/>
            • <strong>Mobile:</strong> Auto-advance pauses during touch interactions<br/>
            • <strong>Accessibility:</strong> Keyboard navigation (arrow keys, spacebar, home/end)<br/>
            • <strong>Accessibility:</strong> Screen reader support with ARIA labels<br/>
            • <strong>Accessibility:</strong> Pause/resume button for manual control<br/>
            • <strong>Accessibility:</strong> Respects reduced motion preferences<br/>
            • <strong>Accessibility:</strong> Image change announcements
          </p>
        </div>

        {/* Single Image */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Single Image (Static display)</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <HeroCarousel
              images={[mockHeroImages[0]]}
              venueName="The Very Long Named Single Image Winery & Tasting Room Experience"
              venueType="tasting_room"
              className="h-80"
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            • No carousel controls for single image<br/>
            • Static display without transitions
          </p>
        </div>

        {/* No Images */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">No Images (Placeholder)</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <HeroCarousel
              images={[]}
              venueName="No Images Winery"
              venueType="winery"
              className="h-80"
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            • Shows placeholder when no images available<br/>
            • Graceful fallback behavior
          </p>
        </div>

        {/* Custom Timing */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Custom Timing (2s interval, 1200ms transition)</h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <HeroCarousel
              images={mockHeroImages}
              venueName="Fast Carousel Winery"
              venueType="wine_bar"
              autoAdvanceInterval={2000}
              transitionDuration={1200}
              className="h-80"
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            • Faster auto-advance (2 seconds)<br/>
            • Longer transition duration (1200ms)<br/>
            • Demonstrates configurable timing
          </p>
        </div>
      </div>

      <div className="mt-12 p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accessibility Features:</h3>
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <h4 className="font-semibold text-primary-900 mb-2">Keyboard Navigation:</h4>
          <ul className="text-primary-800 text-sm space-y-1">
            <li>• <kbd className="px-2 py-1 bg-primary-100 rounded">←</kbd> <kbd className="px-2 py-1 bg-primary-100 rounded">→</kbd> Navigate between images</li>
            <li>• <kbd className="px-2 py-1 bg-primary-100 rounded">Space</kbd> Pause/resume auto-advance</li>
            <li>• <kbd className="px-2 py-1 bg-primary-100 rounded">Home</kbd> Go to first image</li>
            <li>• <kbd className="px-2 py-1 bg-primary-100 rounded">End</kbd> Go to last image</li>
            <li>• <kbd className="px-2 py-1 bg-primary-100 rounded">Tab</kbd> Navigate to carousel controls</li>
          </ul>
        </div>
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-900 mb-2">Screen Reader Support:</h4>
          <ul className="text-green-800 text-sm space-y-1">
            <li>• ARIA labels describe carousel state and controls</li>
            <li>• Live region announces image changes</li>
            <li>• Instructions provided for keyboard navigation</li>
            <li>• Proper role attributes for carousel structure</li>
          </ul>
        </div>
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <h4 className="font-semibold text-primary-900 mb-2">Motion Preferences:</h4>
          <ul className="text-primary-800 text-sm space-y-1">
            <li>• Detects <code>prefers-reduced-motion</code> setting</li>
            <li>• Disables auto-advance for reduced motion</li>
            <li>• Uses instant transitions instead of cross-dissolve</li>
            <li>• Manual pause button always available</li>
          </ul>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-4">Component Features Implemented:</h3>
        <ul className="space-y-2 text-gray-700">
          <li>✅ Cross-dissolve animation with configurable duration (default 800ms)</li>
          <li>✅ Auto-advance functionality with configurable interval (default 4s)</li>
          <li>✅ Hover-to-pause behavior for auto-advance</li>
          <li>✅ Transition conflict prevention (no new transitions during active ones)</li>
          <li>✅ Single image static display mode</li>
          <li>✅ No images placeholder mode</li>
          <li>✅ Proper cleanup of intervals and timeouts</li>
          <li>✅ TypeScript type safety with Media interface</li>
          <li>✅ Responsive design with Tailwind CSS</li>
          <li>✅ Development debug information (in dev mode only)</li>
          <li>✅ Navigation dots indicating current position and total count</li>
          <li>✅ Left/right arrow navigation with click handlers</li>
          <li>✅ Carousel wraparound logic (last→first, first→last)</li>
          <li>✅ Navigation dot click handlers for direct image selection</li>
          <li>✅ Navigation controls styled for both desktop and mobile</li>
          <li>✅ Accessibility features (ARIA labels, focus states, keyboard support)</li>
          <li>✅ Text overlay with venue name and type display</li>
          <li>✅ Black semi-transparent scrim background for text readability</li>
          <li>✅ Text positioned at bottom-left corner with proper spacing</li>
          <li>✅ Text truncation for long venue names with hover tooltip</li>
          <li>✅ Responsive text sizing for mobile and desktop</li>
          <li>✅ <strong>Mobile touch support:</strong> Swipe left/right gesture navigation</li>
          <li>✅ <strong>Mobile touch support:</strong> Touch-friendly navigation controls (44px minimum)</li>
          <li>✅ <strong>Mobile touch support:</strong> Smooth transitions and performance on mobile</li>
          <li>✅ <strong>Mobile touch support:</strong> Touch gesture conflict resolution with auto-advance</li>
          <li>✅ <strong>Mobile touch support:</strong> Visual feedback during touch interactions</li>
          <li>✅ <strong>Accessibility:</strong> Keyboard navigation support (arrow keys, tab navigation)</li>
          <li>✅ <strong>Accessibility:</strong> ARIA labels and descriptions for screen readers</li>
          <li>✅ <strong>Accessibility:</strong> Reduced motion preference detection and instant transitions</li>
          <li>✅ <strong>Accessibility:</strong> Image change announcements for screen readers</li>
          <li>✅ <strong>Accessibility:</strong> Pause carousel button for users who prefer static content</li>
          <li>✅ <strong>Accessibility:</strong> Focus management and keyboard shortcuts</li>
          <li>✅ <strong>Accessibility:</strong> Screen reader instructions and live regions</li>
        </ul>
      </div>
    </div>
  );
};